import React, { useEffect, useRef, useState } from 'react';

/**
 * TooltipLayer — one app-wide hover/focus tooltip for every button.
 *
 * Mount it ONCE (main.jsx). It watches the whole document, so no button
 * needs to import anything. A control gets a tooltip when it carries any of:
 *
 *   data-tip="…"     preferred — a short "what this does" sentence
 *   aria-label="…"   icon-only buttons already have these for screen readers
 *   title="…"        legacy; converted to data-tip on first hover so the
 *                    browser's slow, unstyled native tooltip never shows
 *
 * Rules:
 *  - Text buttons whose visible label already equals the tip show nothing
 *    (a tooltip that repeats the button is noise, not help).
 *  - Touch devices get nothing (no hover; the native long-press stays).
 *  - 350ms delay on the first hover, instant when moving between buttons
 *    (the "warm" behaviour every desktop app uses), hides on click, scroll,
 *    Escape, or leaving the control.
 *  - Keyboard focus shows it too, so it doubles as an accessible hint.
 */

const SELECTOR = 'button, a[href], [role="button"], [data-tip]';
const SHOW_DELAY_MS = 350;
const WARM_WINDOW_MS = 400;
const GAP = 8;
const MARGIN = 8;

function tipFor(el) {
  if (!el) return '';
  let tip = el.getAttribute('data-tip') || '';
  if (!tip) {
    const title = el.getAttribute('title');
    if (title) {
      // Keep the text, drop the native tooltip.
      el.setAttribute('data-tip', title);
      el.removeAttribute('title');
      tip = title;
    }
  }
  if (!tip) tip = el.getAttribute('aria-label') || '';
  tip = tip.trim();
  if (!tip) return '';
  // Don't repeat a label the user can already read on the button.
  const visible = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (visible && visible.toLowerCase() === tip.toLowerCase()) return '';
  return tip;
}

export default function TooltipLayer() {
  const [tip, setTip] = useState(null); // { text, x, y, place }
  const timerRef = useRef(null);
  const lastHideRef = useRef(0);
  const currentElRef = useRef(null);
  const bubbleRef = useRef(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const hide = () => {
      clearTimer();
      if (currentElRef.current) {
        currentElRef.current = null;
        lastHideRef.current = Date.now();
        setTip(null);
      }
    };

    const place = (el, text) => {
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Measure the bubble if it's already rendered; otherwise estimate.
      const b = bubbleRef.current?.getBoundingClientRect();
      const bw = b?.width || Math.min(260, 20 + text.length * 6.2);
      const bh = b?.height || 30;
      let placeAt = 'top';
      let y = r.top - GAP - bh;
      if (y < MARGIN) {
        placeAt = 'bottom';
        y = r.bottom + GAP;
        if (y + bh > vh - MARGIN) y = Math.max(MARGIN, vh - MARGIN - bh);
      }
      let x = r.left + r.width / 2 - bw / 2;
      x = Math.max(MARGIN, Math.min(x, vw - MARGIN - bw));
      const arrowX = Math.max(10, Math.min(bw - 10, r.left + r.width / 2 - x));
      setTip({ text, x, y, place: placeAt, arrowX });
    };

    const show = (el) => {
      const text = tipFor(el);
      if (!text) {
        hide();
        return;
      }
      currentElRef.current = el;
      place(el, text);
      // Re-place once the real bubble size is known.
      requestAnimationFrame(() => {
        if (currentElRef.current === el) place(el, text);
      });
    };

    const onOver = (e) => {
      if (e.pointerType === 'touch') return;
      const el = e.target?.closest?.(SELECTOR);
      if (!el || el === currentElRef.current) return;
      clearTimer();
      if (!el.matches(SELECTOR)) return;
      const warm = Date.now() - lastHideRef.current < WARM_WINDOW_MS || !!currentElRef.current;
      if (currentElRef.current) {
        currentElRef.current = null;
        setTip(null);
      }
      if (warm) show(el);
      else timerRef.current = setTimeout(() => show(el), SHOW_DELAY_MS);
    };

    const onOut = (e) => {
      const el = e.target?.closest?.(SELECTOR);
      if (!el) return;
      const to = e.relatedTarget;
      if (to && el.contains(to)) return; // still inside the control
      hide();
    };

    const onFocusIn = (e) => {
      const el = e.target?.closest?.(SELECTOR);
      if (!el) return;
      // Only for keyboard focus — mouse clicks also focus, and a tooltip
      // popping on click feels wrong.
      let keyboard = true;
      try {
        keyboard = el.matches(':focus-visible');
      } catch {
        keyboard = false; // very old engines: skip focus tooltips rather than throw
      }
      if (!keyboard) return;
      clearTimer();
      show(el);
    };

    const onFocusOut = () => hide();
    const onKey = (e) => {
      if (e.key === 'Escape') hide();
    };
    const onScroll = () => hide();
    const onDown = () => hide();

    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerout', onOut, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      clearTimer();
      document.removeEventListener('pointerover', onOver, true);
      document.removeEventListener('pointerout', onOut, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  if (!tip) return null;

  return (
    <div
      ref={bubbleRef}
      role="tooltip"
      style={{ left: tip.x, top: tip.y }}
      className="fixed z-[10000] pointer-events-none select-none max-w-[260px] px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium leading-snug shadow-lg shadow-slate-900/20 animate-[tipIn_120ms_ease-out]"
    >
      {tip.text}
      <span
        aria-hidden="true"
        style={{ left: tip.arrowX }}
        className={`absolute w-2 h-2 bg-slate-900 rotate-45 -translate-x-1/2 ${
          tip.place === 'top' ? '-bottom-1' : '-top-1'
        }`}
      />
    </div>
  );
}
